import {
  Component,
  OnInit,
  AfterViewInit,
  Input,
  Output,
  EventEmitter,
  ViewChildren,
  QueryList,
  ElementRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  forwardRef,
  ViewEncapsulation,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BrowserService } from 'projects/shared/services/browser.service';

export type OtpMessageType = '' | 'error' | 'success';

export interface OtpCompleteEvent {
  code: string;
}

@Component({
  selector: 'app-otp-input',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './otp-input.component.html',
  styleUrls: ['./otp-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OtpInputComponent),
      multi: true
    }
  ],
})
export class OtpInputComponent implements OnInit, AfterViewInit, ControlValueAccessor, OnChanges {
  code = '';

  admin!: boolean;

  /** Number of OTP digits */
  @Input() length = 6;

  /** Emitted when all digits are filled */
  @Output() otpComplete = new EventEmitter<OtpCompleteEvent>();

  /** Emitted when user requests a resend */
  @Output() otpResend = new EventEmitter<void>();

  @ViewChildren('otpBox') otpBoxes!: QueryList<ElementRef<HTMLInputElement>>;

  digits: string[] = [];
  message = '';
  messageType: OtpMessageType = '';
  shakeTrigger = false;

  // Value Accessor
  onChange = (_: any) => { };
  onTouched = () => { };

  constructor(
    private cdr: ChangeDetectorRef,
    private _browserService: BrowserService
  ) {
    //? 5000 port of localhost for works 
    this.admin = !!_browserService.getLocation()?.href.includes('admin') || !!_browserService.getLocation()?.href.includes('5000')
  }

  // ─────────────────────────────────────────────
  // Lifecycle
  // ─────────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    console.log('changes: ', changes);

  }
  ngOnInit(): void {
    this.digits = Array(this.length).fill('');
  }

  ngAfterViewInit(): void {
    // Focus first box after the view is ready
    this.focusInput(0);
  }

  // ─────────────────────────────────────────────
  // Computed helpers
  // ─────────────────────────────────────────────

  get isComplete(): boolean {
    return this.getCode().length === this.length;
  }

  getCode(): string {
    return this.digits.join('');
  }

  trackByIndex(index: number): number {
    return index;
  }

  // ─────────────────────────────────────────────
  // Event handlers
  // ─────────────────────────────────────────────

  handleInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d).toString());
    value = value.replace(/[^0-9]/g, '').slice(-1);

    this.digits[index] = value;
    input.value = value;

    this.clearMessage();

    this.onChange(this.getCode());

    if (value && index < this.length - 1) {
      this.focusInput(index + 1);
    }

    if (this.isComplete) {
      this.otpComplete.emit({ code: this.getCode() });
    }

    this.cdr.markForCheck();
  }

  handleKeydown(event: KeyboardEvent, index: number): void {
    const inputs = this.otpBoxes.toArray();

    if (event.key === 'Backspace' && !inputs[index].nativeElement.value && index > 0) {
      this.digits[index - 1] = '';
      inputs[index - 1].nativeElement.value = '';
      this.focusInput(index - 1);
      this.cdr.markForCheck();
    } else if (event.key === 'ArrowLeft' && index > 0) {
      this.focusInput(index - 1);
    } else if (event.key === 'ArrowRight' && index < this.length - 1) {
      this.focusInput(index + 1);
    }
  }

  handlePaste(event: ClipboardEvent): void {
    event.preventDefault();
    let text = event.clipboardData?.getData('text').replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d).toString()) ?? '';
    text = text.replace(/[^0-9]/g, '');
    const inputs = this.otpBoxes.toArray();

    text
      .slice(0, this.length)
      .split('')
      .forEach((char, i) => {
        this.digits[i] = char;
        inputs[i].nativeElement.value = char;
      });

    const nextIndex = Math.min(text.length, this.length - 1);
    this.focusInput(nextIndex);

    // ✅ أبلغ Angular بالقيمة بعد الـ paste
    this.onChange(this.getCode());

    if (this.isComplete) {
      this.otpComplete.emit({ code: this.getCode() });
    }

    this.cdr.markForCheck();
  }

  handleFocus(event: FocusEvent): void {
    (event.target as HTMLInputElement).select();
    this.onTouched();
  }

  handleResend(): void {
    this.reset();
    this.setMessage('تم إرسال رمز جديد', 'success');
    this.otpResend.emit();
  }

  // ─────────────────────────────────────────────
  // Public API
  // ─────────────────────────────────────────────

  /** Reset all digits and messages */
  reset(): void {
    this.digits = Array(this.length).fill('');
    this.otpBoxes?.forEach((ref) => {
      ref.nativeElement.value = '';
    });
    this.clearMessage();
    this.focusInput(0);
    this.cdr.markForCheck();
  }

  /** Show an external error (e.g. from API response) */
  showError(message: string): void {
    this.setMessage(message, 'error');
    this.triggerShake();
    this.cdr.markForCheck();
  }

  // ─────────────────────────────────────────────
  // Private helpers
  // ─────────────────────────────────────────────

  private focusInput(index: number): void {
    setTimeout(() => {
      this.otpBoxes?.toArray()[index]?.nativeElement.focus({ preventScroll: true });
    });
  }

  private setMessage(text: string, type: OtpMessageType): void {
    this.message = text;
    this.messageType = type;
    this.cdr.markForCheck();
  }

  private clearMessage(): void {
    this.message = '';
    this.messageType = '';
  }

  private triggerShake(): void {
    this.shakeTrigger = true;
    this.cdr.markForCheck();
    setTimeout(() => {
      this.shakeTrigger = false;
      this.cdr.markForCheck();
    }, 300);
  }

  // Value Accessor

  writeValue(val: string) {
    const str = val ?? '';
    this.digits = Array.from({ length: this.length }, (_, i) => str[i] ?? '');

    if (this.otpBoxes) {
      this.otpBoxes.toArray().forEach((ref, i) => {
        ref.nativeElement.value = this.digits[i];
      });
    }

    this.cdr.markForCheck();
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }
}